













// @ts-ignore
import { getInstance } from "@/auth/auth0";
import { getUserData } from "@/auth/getUserData";
import useActiveChatTemplates from "@/composables/useActiveChatTemplates";
import { PublicApiEndpointPathType } from "@/composables/v2.1/useInboxModuleType";
import useStoredMessages from "@/composables/v2.1/useStoredMessages";
import ChannelInterface from "@/interfaces/channel.interface";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
// @ts-ignore
import router from "@/router";
import { InboxModuleType } from "@/types/inboxModule.type";
import { PlatformType } from "@/types/platform.type";
import { ProviderType } from "@/types/provider.type";
import { InboxModulePathType } from "@saysimple-platform/dtos";
import { ref } from "@vue/composition-api";
import { BSpinner } from "bootstrap-vue";
import * as EmailValidator from "email-validator";
import { isPossiblePhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BSpinner,
    },
    setup() {
        type LocalChannelDto = Omit<ChannelInterface, "platform" | "provider" | "id"> & {
            id: number;
            platform: PlatformType;
            provider: ProviderType;
        };
        type PossibleParameters = {
            contactId?: string;
            userId?: string;
            channelId?: string;
            channelIdentity?: string;
            customerIdentity?: string;
            template?: string;
            variables?: string;
        };
        const toast = useToast();
        const error = ref("");

        const { setStoredMessages } = useStoredMessages();

        const startConversation = async () => {
            // Get the query parameters passed with the url
            const queryParameters: PossibleParameters = new Proxy(new URLSearchParams(window.location.search), {
                get: (searchParams, key: string) => searchParams.get(key),
            }) as PossibleParameters;

            // Define the three variables we need to start a chat and give them a value if we got one
            let contactId = queryParameters.contactId ? Number(queryParameters.contactId) : undefined;
            let userId = queryParameters.userId ? Number(queryParameters.userId) : undefined;
            let channelId = queryParameters.channelId ? Number(queryParameters.channelId) : undefined;

            let templateText = "";
            let channel: LocalChannelDto;

            if (!userId) {
                userId = getUserData()?.id;
                if (!userId) {
                    error.value = i18n.t("start-conversation.unknown-user");
                    toast.error(i18n.t("start-conversation.unknown-user"));
                    return;
                }
            }

            if (!channelId) {
                let channelIdentity = queryParameters.channelIdentity;

                if (!channelIdentity) {
                    error.value = i18n.t("start-conversation.missing-param");
                    toast.error(i18n.t("start-conversation.missing-param"));
                    return;
                }

                const isPhoneNumber = isPossiblePhoneNumber(channelIdentity);

                if (isPhoneNumber) {
                    try {
                        channelIdentity = parsePhoneNumber(channelIdentity).formatInternational();
                    } catch {
                        error.value = i18n.t("start-conversation.incorrectly-formatted-param", {
                            parameter: "channelIdentity",
                        });
                        toast.error(i18n.t("start-conversation.missing-param", { parameter: "channelIdentity" }));
                        return;
                    }
                }

                const channels = await axios
                    .get("/channels", {
                        params: {
                            s: {
                                $and: [
                                    {
                                        identity: {
                                            $eq: channelIdentity,
                                        },
                                    },
                                ],
                            },
                        },
                    })
                    .then((result) => result.data);

                if (!channels || channels.length > 0) {
                    error.value = i18n.t("start-conversation.unknown-channel");
                    toast.error(i18n.t("start-conversation.unknown-channel"));
                    return;
                }
                if (channels.length > 1) {
                    error.value = i18n.t("start-conversation.channel-identity-not-unique");
                    toast.error(i18n.t("start-conversation.channel-identity-not-unique"));
                    return;
                }
                channel = channels[0];

                channelId = channel.id;
            } else {
                channel = await axios
                    .get(`/channels/${channelId}`, {
                        params: {
                            fields: "platform,provider",
                        },
                    })
                    .then((result) => result.data);
            }

            if (!contactId) {
                // We need the platform of the channel so if we didn't get it yet, we get it here.
                if (!channel) {
                    channel = await axios.get(`/channels/${channelId}`).then((result) => result.data);
                }

                const phoneNumberPlatforms = ["whatsapp", "test"];
                const phoneNumberProviders = ["test", "cm", "twilio", "messagebird", "whatsapp", "bird"];

                let targetIdentity = queryParameters.customerIdentity;
                if (!targetIdentity) {
                    error.value = i18n.t("start-conversation.missing-param");
                    toast.error(i18n.t("start-conversation.missing-param"));
                    return;
                }

                let isPhoneNumber = false;
                if (
                    phoneNumberPlatforms.includes(channel.platform) &&
                    phoneNumberProviders.includes(channel.provider)
                ) {
                    try {
                        targetIdentity = parsePhoneNumber(targetIdentity).formatInternational();
                    } catch {
                        error.value = i18n.t("start-conversation.incorrectly-formatted-param", {
                            parameter: "customerIdentity",
                        });
                        toast.error(
                            i18n.t("start-conversation.incorrectly-formatted-param", { parameter: "customerIdentity" })
                        );
                        return;
                    }
                    isPhoneNumber = true;
                }

                if (channel.platform === PlatformType.EMAIL) {
                    const isEmail = EmailValidator.validate(targetIdentity);

                    if (!isEmail) {
                        error.value = i18n.t("start-conversation.incorrectly-formatted-param", {
                            parameter: "customerIdentity",
                        });
                        toast.error(
                            i18n.t("start-conversation.incorrectly-formatted-param", { parameter: "customerIdentity" })
                        );
                        return;
                    }
                }

                let identity = await axios
                    .get("/identities", {
                        params: {
                            fields: "id,contactId",
                            s: {
                                $and: [
                                    {
                                        identity: {
                                            $eq: targetIdentity,
                                        },
                                    },
                                    {
                                        channelId: {
                                            $eq: channelId,
                                        },
                                    },
                                ],
                            },
                        },
                    })
                    .then((result) => result.data[0]);

                // If there is no contactId then we need to create a contact. We might even need to create an identity altogether
                if (!identity?.contactId) {
                    // First search if a contact already exist
                    let contact = await axios
                        .get("/contacts", {
                            params: {
                                s: {
                                    $or: [
                                        {
                                            phone: targetIdentity,
                                        },
                                        {
                                            email: targetIdentity,
                                        },
                                    ],
                                },
                            },
                        })
                        .then((result) => result.data[0]);

                    if (!contact) {
                        // Otherwise create a new contact
                        contact = await axios
                            .post("/contacts", {
                                name: targetIdentity,
                                phone: isPhoneNumber ? targetIdentity : undefined,
                                email: EmailValidator.validate(targetIdentity) ? targetIdentity : undefined,
                            })
                            .then((result) => result.data);
                    }

                    if (!identity) {
                        identity = await axios
                            .post("/identities", {
                                identity: targetIdentity,
                                channelId: channelId,
                                contactId: contact.id,
                                platform: channel.platform.toLowerCase(),
                            })
                            .then((result) => result.data);
                    } else {
                        identity = await axios
                            .patch(`/identities/${identity.id}`, {
                                id: identity.id,
                                contactId: contact.id,
                            })
                            .then((result) => result.data);
                    }
                }

                contactId = identity.contactId;
            }

            // Get the template data and replace variables if applicable
            if (queryParameters.template) {
                const template = await axios
                    .get("/templates", {
                        params: {
                            join: "channels||id",
                            fields: "content",
                            filter: [`title||$eq||${queryParameters.template}`, `channels.id||$eq||${channelId}`],
                        },
                    })
                    .then((result) => result.data[0]);

                if (!template) {
                    error.value = i18n.t("start-conversation.unknown-template");
                    toast.error(i18n.t("start-conversation.unknown-template"));
                    return;
                }
                const { mapVariablesValues, replaceVariablesByObject } = useActiveChatTemplates();

                const variablesToPlace = JSON.parse(queryParameters.variables || "[]");

                templateText = replaceVariablesByObject(template.content, await mapVariablesValues(contactId));
                templateText = templateText.replace(/{{\s*([^}]*)\s*}}/g, (match) => variablesToPlace.shift() || match);
            }

            // Make a request to the backend to start the chat for us
            return axios
                .post(
                    channel.platform === PlatformType.EMAIL
                        ? PublicApiEndpointPathType.EMAIL
                        : PublicApiEndpointPathType.MESSAGING,
                    { contactId, channelId, userId, skipPostActions: true }
                )
                .then((conversation) => {
                    toast.success(i18n.t("entities.conversation.started"));

                    const inboxModuleType =
                        channel.platform === PlatformType.EMAIL ? InboxModuleType.EMAIL : InboxModuleType.MESSAGING;

                    setStoredMessages({ chatId: conversation.data.id, message: templateText, inboxModuleType });

                    // Redirect to inbox with the conversation we got back
                    router.replace({
                        name:
                            channel.platform === PlatformType.EMAIL
                                ? InboxModulePathType.EMAIL
                                : InboxModulePathType.MESSAGING,
                        params: {
                            chatId: conversation.data.id,
                        },
                    });
                })
                .catch(() => {
                    error.value = i18n.t("entities.conversation.could-not-start");
                    toast.error(i18n.t("entities.conversation.could-not-start"));
                });
        };

        const instance = getInstance();
        // We have to wait until auth0 is done loading. Else we might not have a token yet to send with the requests
        if (!instance || instance.loading) {
            instance.$watch("loading", (isLoading: boolean) => {
                if (isLoading === false) {
                    startConversation().catch(() => {
                        error.value = i18n.t("start-conversation.error");
                        toast.error(i18n.t("start-conversation.error"));
                    });
                }
            });
        } else {
            startConversation().catch(() => {
                error.value = i18n.t("start-conversation.error");
                toast.error(i18n.t("start-conversation.error"));
            });
        }

        return {
            error,
        };
    },
};
